<template>
  <div class="bg-success pb-16 pt-8">
    <LayoutsSectionRegularTemplate :dark="true" class="bg-global-filter">
      <template #icon>
        <ReviewIcon size="48" class="mx-auto" />
      </template>
      <template #title>
        <span v-html="dataLocale.data.title" />
      </template>
      <template #content>
        <div
          class="w-full rounded-2xl drop-shadow-xl mt-10 lg:mt-16 max-w-4xl px-8 pt-8 pb-8 mx-auto sm:pr-8 sm:pl-45px bg-white relative"
        >
          <div class="mb-6">
            <QuoteIcon />
          </div>
          <div class="embla" ref="emblaNode">
            <div class="embla__container">
              <div class="embla__slide" v-for="review in reviews">
                <div class="mr-4 sm:mr-16 text-lg sm:text-xl">
                  "{{ review.content }}"
                </div>
                <div class="flex items-center gap-2 mt-8">
                  <StarFilledIcon v-for="i in 5" class="text-yellow-300" />
                </div>
                <div
                  class="flex flex-wrap items-center mt-4 space-x-2 md:space-x-3 text-base"
                >
                  <nuxt-img
                    loading="lazy"
                    :src="review.icon"
                    format="webp"
                    :alt="review.name"
                    :width="60"
                  />
                  <div class="flex gap-2 flex-col">
                    <span class="w-full md:w-auto font-semibold">{{
                      review.name
                    }}</span>
                    <span class="w-full md:w-auto opacity-75">{{
                      review.job
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            class="sm:absolute sm:mt-0 mt-4 bottom-8 right-8 flex items-center gap-2"
          >
            <button
              class="embla__prev group w-10 h-10 flex items-center justify-center rounded-full border border-black text-black hover:bg-black opacity-25 hover:opacity-100 hover:text-white bg-white"
              @click="scrollPrev()"
            >
              <ArrowLeftIcon
                class="group-active:ml-1 transition-all"
                stroke-width="1"
                size="24"
              />
              <span class="sr-only">Previous</span>
            </button>
            <button
              class="embla__next group w-10 h-10 flex items-center justify-center rounded-full border border-black text-black hover:bg-black opacity-25 hover:opacity-100 hover:text-white bg-white"
              @click="scrollNext()"
            >
              <ArrowRightIcon
                class="group-active:ml-1 transition-all"
                stroke-width="1"
                size="24"
              />
              <span class="sr-only">Next</span>
            </button>
          </div>
        </div>
      </template>
    </LayoutsSectionRegularTemplate>
  </div>
</template>
<script lang="ts" setup>
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  StarFilledIcon,
} from "vue-tabler-icons";
import ReviewIcon from "~/assets/svg/review.svg?component";
import QuoteIcon from "~/assets/svg/quote.svg?component";
import StarIcon from "~/assets/svg/star.svg?component";
import emblaCarouselVue from "embla-carousel-vue";
import Autoplay from "embla-carousel-autoplay";
import ReviewLocale from "~~/config/widgets/reviewer-widget";
import DataLocale from "~~/config/interface/DataLocale";

const reviews = new ReviewLocale().data;
const [emblaNode, emblaApi] = emblaCarouselVue({ loop: false }, [
  Autoplay({ delay: 4000 }),
]);
const slideIndex = ref(0);
const scrollPrev = () => {
  emblaApi.value?.scrollPrev();
};

const scrollNext = () => {
  emblaApi.value?.scrollNext();
};
const dataLocale = new DataLocale([
  {
    locale: "en",
    title:
      "What Our Clients Say <br> <span class='down-mark-line'> About Us?</span>",
  },
  {
    locale: "vi",
    title:
      "Đánh giá của khách hàng về chúng tôi<br> <span class='down-mark-line'> là gì?</span>",
  },
  {
    locale: "de",
    title:
      "Was unsere Kunden sagen <br> <span class='down-mark-line'> Über uns?</span>",
  },
  {
    locale: "nl",
    title:
      "Wat zeggen onze klanten <br> <span class='down-mark-line'> Over ons?</span>",
  },
  {
    locale: "it",
    title:
      "Cosa dicono i nostri clienti <br> <span class='down-mark-line'> Su di noi?</span>",
  },
  {
    locale: "es",
    title:
      "Lo que dicen nuestros clientes <br> <span class='down-mark-line'> Sobre nosotros?</span>",
  },
  {
    locale: "pt",
    title:
      "O que nossos clientes dizem <br> <span class='down-mark-line'> Sobre nós?</span>",
  },
  {
    locale: "ja",
    title:
      "私たちのお客様の声<br><span class='down-mark-line'>私たちについての感想</span>",
  },
  {
    locale: "fr",
    title:
      "Ce que disent nos clients <br> <span class='down-mark-line'> À propos de nous?</span>",
  },
  {
    locale: "zh",
    title:
      "我们客户的评价<br><span class='down-mark-line'>关于我们的评价</span>",
  },
  {
    locale: "tr",
    title:
      "Müşterilerimiz Ne Diyor <br> <span class='down-mark-line'>Hakkımızda?</span>",
  },
  {
    locale: "hi",
    title:
      "हमारे ग्राहक क्या कहते हैं <br> <span class='down-mark-line'>हमारे बारे में?</span>",
  },
  {
    locale: "id",
    title:
      "Apa Kata Klien Kami <br> <span class='down-mark-line'>Tentang Kami?</span>",
  },
  {
    locale: "th",
    title:
      "ลูกค้าของเราพูดอะไร <br> <span class='down-mark-line'>เกี่ยวกับเรา?</span>",
  },
]);
</script>
<style>
.bg-global-filter {
  background-image: url(/images/global.png);
  background-size: contain;
  background-repeat: no-repeat;
  background-position: top;
}
</style>
<style scoped>
.embla {
  overflow: hidden;
}

.embla__container {
  display: flex;
}

.embla__slide {
  flex: 0 0 100%;
  min-width: 0;
}
</style>
